/**
 * METABALL-HALS — die flüssige Brücke zwischen zwei Kreisen, damit die
 * Kapitel-Punkte von Punkt zu Punkt „schlonzen“ (Jans Wunsch 06.10.2026).
 *
 * Geometrie nach dem bekannten Paper.js-Metaball-Beispiel (Hiroyuki Sato):
 * je zwei Ansatzpunkte auf den Kreisrändern, verbunden durch zwei
 * Bézierkurven mit Taille. Anders als dort wird der Hals mit wachsendem
 * Abstand immer dünner und reißt an der Reichweite ohne Sprung ab — bei
 * Punkten von 6–16 px wäre ein Weichzeichner-Filter nur Matsch.
 */

export interface Kreis {
    x: number;
    y: number;
    r: number;
}

interface Pkt {
    x: number;
    y: number;
}

const hin = (p: Pkt, winkel: number, laenge: number): Pkt => ({
    x: p.x + Math.cos(winkel) * laenge,
    y: p.y + Math.sin(winkel) * laenge,
});

const zahl = (p: Pkt) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;

/**
 * SVG-Pfad des Halses zwischen a und b — leer, wenn keiner zu sehen ist (zu
 * weit auseinander, ein Kreis steckt ganz im anderen, Radius 0). Die Kreise
 * selbst zeichnet der Aufrufer.
 *
 * @param reichweite Abstand der Kreisränder, bei dem der Hals reißt
 *                   (Infinity: reißt nie)
 * @param breite     0…1: wie breit der Hals an den Kreisen ansetzt
 */
export function hals(a: Kreis, b: Kreis, reichweite: number, breite = 0.5): string {
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const summe = a.r + b.r;
    if (a.r <= 0 || b.r <= 0 || d <= Math.abs(a.r - b.r) || d >= summe + reichweite) return "";

    // Überlappen sich die Kreise, setzt der Hals an ihren Schnittpunkten an
    let u1 = 0;
    let u2 = 0;
    if (d < summe) {
        u1 = Math.acos((a.r * a.r + d * d - b.r * b.r) / (2 * a.r * d));
        u2 = Math.acos((b.r * b.r + d * d - a.r * a.r) / (2 * b.r * d));
    }
    // Auseinander: der Hals wird zur Reichweite hin weich dünner (smoothstep)
    const t = d > summe ? Math.min(1, (d - summe) / reichweite) : 0;
    const v = breite * (1 - t * t * (3 - 2 * t));

    const mitte = Math.atan2(b.y - a.y, b.x - a.x);
    const spreiz = Math.acos((a.r - b.r) / d);
    const w1 = mitte + u1 + (spreiz - u1) * v;
    const w2 = mitte - u1 - (spreiz - u1) * v;
    const w3 = mitte + Math.PI - u2 - (Math.PI - u2 - spreiz) * v;
    const w4 = mitte - Math.PI + u2 + (Math.PI - u2 - spreiz) * v;
    const p1 = hin(a, w1, a.r);
    const p2 = hin(a, w2, a.r);
    const p3 = hin(b, w3, b.r);
    const p4 = hin(b, w4, b.r);

    // Henkel entlang der Kreistangenten, so lang wie der Abstand der Kurvenenden
    // es erlaubt — daraus entsteht die Taille
    const henkel = Math.min(v * 2.4, Math.hypot(p3.x - p1.x, p3.y - p1.y) / summe) * Math.min(1, (2 * d) / summe);
    const h1 = hin(p1, w1 - Math.PI / 2, a.r * henkel);
    const h2 = hin(p2, w2 + Math.PI / 2, a.r * henkel);
    const h3 = hin(p3, w3 + Math.PI / 2, b.r * henkel);
    const h4 = hin(p4, w4 - Math.PI / 2, b.r * henkel);

    // p3→p4 und p2→p1 laufen innen durch die Kreise, die der Aufrufer zeichnet
    return `M${zahl(p1)}C${zahl(h1)} ${zahl(h3)} ${zahl(p3)}L${zahl(p4)}C${zahl(h4)} ${zahl(h2)} ${zahl(p2)}Z`;
}
